define('customersDetailBodyCatalogsView',[
  'module',
  'backbone',
  'underscore',
  'jquery',
  // ensure Backbone.Relational is loaded so RelationalModel/findOrCreate are available
  'settings',
    'rolesMixin',
    'tableView',
  'modules/pages/customers/detail/body/catalog/tools/view',
  'modules/pages/customers/detail/body/catalog/headerTools/view',
    // tools column view (use direct path to avoid editing generated config)
    'backboneRelational'
], function (
    Module,
    Backbone,
    _,
    $, 
    Settings,
    RolesMixin,
    TableView,
    CatalogToolsView,
    CatalogHeaderToolsView
) {
  'use strict';

  // Ensure models in the table use secId as unique identifier to avoid duplicates on refresh
  var CatalogInstance = Backbone.RelationalModel.extend({
    idAttribute: 'secId'
  });

  Module.exports = TableView.extend({
    className: 'customers-detail-body-catalogs-view ' + TableView.prototype.className,
    header: true,
    pageMode: 'pagination',
    pageSize: 50,

    // Map of active generation pollers keyed by catalog secId
    _genPollers: null,

    // Ensure that when the view is shown (including after a hard reload),
    // we automatically detect any catalogs that are currently generating
    // and start polling for them.
    onShow: function () {
      // Call parent onShow to build header/body/footer and kick initial fetch
      try { if (TableView.prototype.onShow) { TableView.prototype.onShow.apply(this, arguments); } } catch (e) {}

      // Bind watchers to the body collection to auto-start polling whenever
      // data arrives or changes
      try { this._bindGenerationWatchers(); } catch (e) {}

      // Do an initial scan shortly after first render to pick up existing
      // in-progress generations on reload
      var that = this;
      setTimeout(function () {
        try { that._scanForActiveGenerations(); } catch (e) {}
      }, 300);
    },

    setFields: function () {
      var fields = [];

      // Catalog name
      fields.push({
        name: 'catalogName',
        label: _.i18n('common.name') || 'Catalog',
        className: 'catalog-name',
        bind: 'productCatalogDefinition',
        modelEvent: 'change',
        formatter: function (value/*, model*/) {
          // value is productCatalogDefinition
          if (!value) return '';
          var name = value.name || {};
          // DynamicText newer format: { values: { en: '...', fr: '...' } }
          var lang = Settings.get('lang') || 'en';
          if (name.values) {
            return name.values[lang] || name.values.en || name.values.fr || '';
          }
          // Fallback to old structure
          if (_.isObject(name)) {
            return name[lang] || name.en || name.fr || '';
          }
          return '' + name;
        }
      });

      // Products count
      fields.push({
        name: 'productsCount',
        label: _.i18n('common.count') || _.i18n('menu.products') || 'Products',
        className: 'products-count',
        bind: 'products',
        modelEvent: 'change',
        formatter: function (value) {
          return (value && value.length) ? value.length : 0;
        }
      });

      // File UID
      fields.push({
        name: 'fileUid',
        label: _.i18n('catalogs.fileUid') || 'File UID',
        className: 'file-uid',
        bind: 'fileUid',
        modelEvent: 'change',
        // Custom onRender to force updating the cell even when fileUid is null
        // (the default FieldView only renders when the bound value is not null/undefined)
        onRender: function(fieldView) {
          try {
            var model = fieldView.model;
            var value = model && model.get && model.get('fileUid');
            // While generating (or any non-terminal in-progress), show nothing
            if (!value) {
              fieldView.$el.html('');
              fieldView.$el.attr('title', '');
              return;
            }

            var url = Settings.url('file', value);
            var title = (_.i18n('common.download') || 'Download');
            var text = title;
            var html = '' +
              '<a class="btn btn-xs btn-default download-link"' +
                ' href="' + _.escape(url) + '"' +
                ' target="_blank" rel="noopener"' +
                ' title="' + _.escape(title) + '">' +
                  '<i class="fa fa-download"></i> ' + text +
              '</a>';
            fieldView.$el.html(html);
            fieldView.$el.attr('title', title);
          } catch (e) {
            // fallback to default behavior on unexpected error
          }
        },
        formatter: function (value, model) {
          // While generating, always clear the file column even if an old UID lingers
          try {
            var status = (model && model.get && model.get('status')) ? (model.get('status') + '').toLowerCase() : '';
            if (status && status.indexOf('generat') === 0) { // generating
              return '';
            }
          } catch (e1) {}

          if (!value) return '';
          try {
            var url = Settings.url('file', value);
            var title = (_.i18n('common.download') || 'Download');
            var text = title; // keep text same as title for consistency
            return '' +
              '<a class="btn btn-xs btn-default download-link"' +
                ' href="' + _.escape(url) + '"' +
                ' target="_blank" rel="noopener"' +
                ' title="' + _.escape(title) + '">' +
                  '<i class="fa fa-download"></i> ' + text +
              '</a>';
          } catch (e) {
            return value || '';
          }
        }
      });

      // Status
      fields.push({
        name: 'status',
        label: _.i18n('common.status') || 'Status',
        className: 'status',
        bind: 'status',
        modelEvent: 'change',
        formatter: function (value) {
          try {
            var s = (value || '').toString();
            var lower = s.toLowerCase();
            // Consider any status starting with "generat" as generating
            if (lower.indexOf('generating') === 0) {
              var label = _.i18n('catalogs.generating') || 'Generating…';
              return '' +
                '<span class="text-info" title="' + _.escape(label) + '">' +
                  '<i class="fa fa-spinner fa-spin" style="margin-right:6px;"></i>' +
                  _.escape(label) +
                '</span>';
            }
            return s;
          } catch (e) {
            return value || '';
          }
        }
      });

      fields.push({
        name: 'tools',
        label: _.i18n('common.tools') || 'Tools',
        className: 'tools text-right',
        view: CatalogToolsView,
        headerView: CatalogHeaderToolsView,
        onRender: function () {}
      });

      return fields;
    },

    // ---------- Generation polling helpers ----------
    _isTerminalStatus: function (status/*, fileUid*/) {
      try {
        // Only stop on explicit terminal outcomes per requirement
        // Ignore presence of fileUid and any intermediate statuses like "generating"
        var s = (status || '').toString().toLowerCase().trim();
        return s === 'generated' || s === 'failed';
      } catch (e) {
        return false;
      }
    },

    // Refresh only a single row (model) by secId without reloading the whole table
    // Returns a jQuery Promise
    refreshRow: function (secId) {
      var defer = $.Deferred();
      try {
        var collection = this.bodyView && this.bodyView.collection;
        if (!secId || !collection) {
          defer.resolve();
          return defer.promise();
        }

        var url = Settings.url('compuzz2', 'v2/catalog/instances/' + encodeURIComponent(secId));
        $.ajax({ type: 'GET', url: url })
          .done(_.bind(function (item) {
            try {
              if (item && item.secId) {
                  item.fileUid = item.fileUid || '';
                var m = null;
                try {
                  m = CatalogInstance.findOrCreate(item, { update: true, merge: true, collection: collection });
                } catch (e1) {
                  m = new CatalogInstance(item);
                }
                if (collection && m && !collection.get(m)) {
                  collection.add(m);
                }
              }
            } catch (e) { /* noop */ }
            defer.resolve();
          }, this));
      } catch (e) {
        defer.resolve();
      }
      return defer.promise();
    },

    startGenerationPolling: function (secId, options) {
      options = options || {};
      var intervalMs = options.intervalMs || 5000;
      var maxMs = options.maxMs || 5 * 60 * 1000; // 5 minutes safety timeout

      if (!this._genPollers) this._genPollers = {};
      // Prevent duplicates
      if (this._genPollers[secId]) {
        return;
      }

      var that = this;
      var startTime = Date.now();

      function stop() {
        var p = that._genPollers && that._genPollers[secId];
        if (p) {
          try { clearInterval(p.intervalId); } catch (e) {}
          try { clearTimeout(p.timeoutId); } catch (e) {}
          delete that._genPollers[secId];
        }
      }

      function checkAndMaybeStop() {
        try {
          if (!that || !that.$ || that.isDestroyed === true) { stop(); return; }
          // Find model in current table collection
          var collection = (that.bodyView && that.bodyView.collection) || null;
          if (collection) {
            var model = collection.get(secId);
            if (model) {
              var status = model.get('status');
              var fileUid = model.get('fileUid');
              if (that._isTerminalStatus(status, fileUid)) {
                stop();
              }
            }
          }
        } catch (e) { /* noop */ }
      }

      function tick() {
        var p = null;
        try { if (that.refreshRow) { p = that.refreshRow(secId); } } catch (e) { p = null; }
        if (p && p.always) {
          p.always(checkAndMaybeStop);
        } else {
          setTimeout(checkAndMaybeStop, 500);
        }
        // Safety timeout check
        if (Date.now() - startTime > maxMs) {
          stop();
        }
      }

      // Start interval and safety timeout
      var intervalId = setInterval(tick, intervalMs);
      var timeoutId = setTimeout(function () { stop(); }, maxMs + 1000);
      this._genPollers[secId] = { intervalId: intervalId, timeoutId: timeoutId };

      // Immediate run
      tick();
    },

    stopAllGenerationPolling: function () {
      if (!this._genPollers) return;
      for (var key in this._genPollers) {
        if (Object.prototype.hasOwnProperty.call(this._genPollers, key)) {
          var p = this._genPollers[key];
          try { clearInterval(p.intervalId); } catch (e) {}
          try { clearTimeout(p.timeoutId); } catch (e) {}
        }
      }
      this._genPollers = {};
    },

    // Watch the table collection for updates and scan for generating items
    _bindGenerationWatchers: function () {
      try {
        if (!this.bodyView || !this.bodyView.collection) return;
        // Avoid binding multiple times
        if (this._genWatchersBound) return;
        this._genWatchersBound = true;
        var collection = this.bodyView.collection;
        // Marionette/Backbone triggers: update, reset, add, change
        this.listenTo(collection, 'update reset add change', _.bind(function () {
          try { this._scanForActiveGenerations(); } catch (e) {}
        }, this));
      } catch (e) {}
    },

    // Iterate models and start polling for those in a non-terminal state
    _scanForActiveGenerations: function () {
      try {
        var collection = this.bodyView && this.bodyView.collection;
        if (!collection || !collection.each) return;
        var that = this;
        collection.each(function (m) {
          try {
            var secId = m && m.get && m.get('secId');
            if (!secId) return;
            var status = m.get('status');
            var fileUid = m.get('fileUid');
            // If not in a terminal state, ensure polling is active
            if (!that._isTerminalStatus(status, fileUid)) {
              that.startGenerationPolling(secId, { intervalMs: 5000 });
            }
          } catch (e) {}
        });
      } catch (e) {}
    },

    // Align with TableView contract like webshop/categories list
    // fetch(first, pageSize, sortField, sortDirection, filters)
    fetch: function (first, pageSize, sortField, sortDirection, filters) {
      var defer = $.Deferred();
      var endUserSecId = this.model && this.model.get && this.model.get('secId');
      if (!endUserSecId) {
        defer.resolve([]);
        return defer.promise();
      }

      if (this.fetchRequest) this.fetchRequest.abort();

      // Build params (currently only endUserSecId is supported by API)
      var params = this.getFiltersParams(filters);
      params.endUserSecId = endUserSecId;

      this.fetchRequest = $.ajax({
        type: 'GET',
        url: Settings.url('compuzz2', 'v2/catalog/instances', params),
        success: _.bind(function (items) {
          var that = this;
          var collection = (that.bodyView && that.bodyView.collection) || null;
          var models = _.map(items || [], function (it) {
            try {
              var m = CatalogInstance.findOrCreate(it, { update: true, merge: true, collection: collection });
              if (collection && !collection.get(m)) {
                collection.add(m);
              }
              return m;
            } catch (e) {
              return new CatalogInstance(it);
            }
          });

          // Apply client-side pagination if needed
          if (typeof first === 'number' && typeof pageSize === 'number') {
            models = models.slice(first, first + pageSize);
          }

          defer.resolve(models);
        }, this),
        error: function () { defer.resolve([]); }
      });

      return defer.promise();
    },

    // Return the total count to drive paginator; if no dedicated count endpoint,
    // reuse list call and return its length.
    fetchCount: function (filters) {
      var defer = $.Deferred();
      var endUserSecId = this.model && this.model.get && this.model.get('secId');
      if (!endUserSecId) {
        defer.resolve(0);
        return defer.promise();
      }

      if (this.fetchCountRequest) this.fetchCountRequest.abort();

      var params = this.getFiltersParams(filters);
      params.endUserSecId = endUserSecId;

      this.fetchCountRequest = $.ajax({
        type: 'GET',
        url: Settings.url('compuzz2', 'v2/catalog/instances', params),
        success: function (items) {
          var count = (items && items.length) ? items.length : 0;
          defer.resolve(count);
        },
        error: function () { defer.resolve(0); }
      });

      return defer.promise();
    },

    // Keep a filters params builder to mirror categories list pattern
    getFiltersParams: function (filters) {
      var params = {};
      try {
        if (filters && filters.attributes) {
          _.each(filters.attributes, function (value, key) {
            if (value !== '') params[key] = value;
          });
        }
      } catch (e) {}
      return params;
    },

    onDestroy: function () {
      try { this.stopAllGenerationPolling(); } catch (e) {}
      // Call parent onDestroy if defined
      try { if (TableView.prototype.onDestroy) { TableView.prototype.onDestroy.apply(this, arguments); } } catch (e) {}
    }
  });
});

